/**
 * details-accordion custom element replacing <details> for smooth CSS transitions.
 * Usage: <details-accordion [open]> <summary> ... </summary> <div class="accordion__content"> ... </div> </details-accordion>
 */
if (!customElements.get('details-accordion')) {
  customElements.define('details-accordion', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this._init());
    }

    disconnectedCallback() {
      this._destroy();
    }

    reinit() {
      this._destroy();
      this._init();
    }

    _destroy() {
      this._clearTransitionHandler();
      this._attrObserver?.disconnect();
      this._attrObserver = null;

      if (this._summary) {
        if (this._onSummaryClickHandler) {
          this._summary.removeEventListener('click', this._onSummaryClickHandler);
        }
        if (this._onKeyDownHandler) {
          this._summary.removeEventListener('keydown', this._onKeyDownHandler);
        }
      }

      this._summary = null;
      this._content = null;
      this._initialized = false;
    }

    _init() {
      if (this._initialized) return;

      this._summary = this.querySelector(':scope > summary');
      this._content = this.querySelector(':scope > .accordion__content');

      if (!this._summary || !this._content) return;

      if (this._isOpen === undefined) {
        this._isOpen = this.hasAttribute('open');
      } else {
        this._syncOpenAttribute();
      }

      this._applyHeight(this._isOpen ? 'auto' : '0px');

      this._onSummaryClickHandler = (event) => {
        if (this._shouldIgnoreToggle()) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        this._toggle();
      };

      this._onKeyDownHandler = (event) => {
        if (this._shouldIgnoreToggle()) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._toggle();
        }
      };

      this._summary.addEventListener('click', this._onSummaryClickHandler);
      this._summary.addEventListener('keydown', this._onKeyDownHandler);

      this._attrObserver = new MutationObserver(() => {
        const nowOpen = this.hasAttribute('open');
        if (nowOpen && !this._isOpen) {
          this._open(true);
        } else if (!nowOpen && this._isOpen) {
          this._close(true);
        }
      });
      this._attrObserver.observe(this, { attributes: true, attributeFilter: ['open'] });

      this._summary.setAttribute('role', 'button');
      if (!this._summary.hasAttribute('tabindex')) {
        this._summary.setAttribute('tabindex', '0');
      }
      this._summary.setAttribute('aria-expanded', this._isOpen ? 'true' : 'false');

      if (this._content.id) {
        this._summary.setAttribute('aria-controls', this._content.id);
      }

      this._initialized = true;
    }

    _shouldIgnoreToggle() {
      return (
        this.classList.contains('footer-accordion') &&
        window.innerWidth >= 750 &&
        this.dataset.footerDesktop === 'true'
      );
    }

    _syncOpenAttribute() {
      if (this._isOpen) {
        this.setAttribute('open', '');
      } else {
        this.removeAttribute('open');
      }
    }

    syncOpenState(open) {
      this._clearTransitionHandler();
      this._isOpen = !!open;
      this._syncOpenAttribute();
      this._summary?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this._content) {
        this._applyHeight(open ? 'auto' : '0px');
      }
    }

    _clearTransitionHandler() {
      if (this._transitionFallback) {
        clearTimeout(this._transitionFallback);
        this._transitionFallback = null;
      }

      if (this._transitionEndHandler && this._content) {
        this._content.removeEventListener('transitionend', this._transitionEndHandler);
        this._transitionEndHandler = null;
      }
    }

    _toggle() {
      if (this._isOpen) {
        this._close(false);
      } else {
        this._open(false);
      }
    }

    _open(fromObserver = false) {
      if (this._isOpen) return;

      const productInfoContainer = this.closest('.product__info-container');
      const collapsibleContent = this.closest('.collapsible-content');

      if (productInfoContainer) {
        productInfoContainer.querySelectorAll('.product__accordion details-accordion').forEach((sibling) => {
          if (sibling !== this && sibling._isOpen) {
            sibling._close(false);
          }
        });
      } else if (collapsibleContent) {
        collapsibleContent.querySelectorAll('.accordion details-accordion').forEach((sibling) => {
          if (sibling !== this && sibling._isOpen) {
            sibling._close(false);
          }
        });
      }

      this._clearTransitionHandler();
      this._isOpen = true;

      if (!fromObserver) {
        this._syncOpenAttribute();
      }

      this._summary?.setAttribute('aria-expanded', 'true');

      if (this.classList.contains('mega-menu')) {
        this._applyHeight('auto');
        return;
      }

      if (!this._content) return;

      this._applyHeight('0px');
      this._content.offsetHeight;
      const targetHeight = this._content.scrollHeight;
      this._applyHeight(`${targetHeight}px`);

      this._transitionEndHandler = (event) => {
        if (event.propertyName !== 'height') return;
        if (!this._isOpen) return;
        this._applyHeight('auto');
        this._clearTransitionHandler();
      };
      this._content.addEventListener('transitionend', this._transitionEndHandler);
      this._transitionFallback = setTimeout(() => {
        if (this._isOpen) this._applyHeight('auto');
        this._clearTransitionHandler();
      }, 400);
    }

    _close(fromObserver = false) {
      if (!this._isOpen) return;

      this._clearTransitionHandler();
      this._isOpen = false;
      this._summary?.setAttribute('aria-expanded', 'false');

      if (!this._content) {
        if (!fromObserver) this.removeAttribute('open');
        return;
      }

      if (this.classList.contains('mega-menu')) {
        this._applyHeight('');
        if (!fromObserver) this.removeAttribute('open');
        return;
      }

      const startHeight = this._content.scrollHeight;
      this._applyHeight(`${startHeight}px`);
      this._content.offsetHeight;
      this._applyHeight('0px');

      if (!fromObserver) {
        this.removeAttribute('open');
      }

      this._transitionEndHandler = (event) => {
        if (event.propertyName !== 'height') return;
        if (this._isOpen) return;
        this._applyHeight('0px');
        this._clearTransitionHandler();
      };
      this._content.addEventListener('transitionend', this._transitionEndHandler);
      this._transitionFallback = setTimeout(() => {
        if (!this._isOpen) this._applyHeight('0px');
        this._clearTransitionHandler();
      }, 400);
    }

    _applyHeight(value) {
      if (this._content) {
        this._content.style.height = value;
      }
    }
  });
}
